/**
 * Handles newsletter re-permission links (?nl_token=<uuid>) on any page.
 * Calls confirm_newsletter_token once (no sign-in needed), strips the param,
 * and shows a localized result toast.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { db } from '@/lib/supabaseQuery';
import { logger } from '@/utils/logger';
import { useLocalizations } from '@/hooks/useLocalizations';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const INITIAL_TOKEN: string | null = ((): string | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('nl_token');
})();

let consumed: string | null = null;

interface ConfirmResult {
  ok: boolean;
  reason?: string;
}

export function useNewsletterTokenConfirmation(): void {
  const { lCommon } = useLocalizations();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    const live = new URL(window.location.href).searchParams.get('nl_token');
    const token = live || INITIAL_TOKEN;
    if (!token || token === consumed) return;
    ran.current = true;
    consumed = token;

    const clean = new URL(window.location.href);
    clean.searchParams.delete('nl_token');
    window.history.replaceState({}, '', clean.pathname + clean.search + clean.hash);

    const fail = (): void => {
      toast.error(lCommon('newsletter_confirm.error', { defaultValue: 'This subscription link is invalid or has expired.' }));
    };
    if (!UUID_RE.test(token)) { fail(); return; }

    void (async (): Promise<void> => {
      const { data, error } = await db.raw.rpc('confirm_newsletter_token', { p_token: token });
      const res = data as unknown as ConfirmResult | null;
      if (error || !res?.ok) {
        logger.warn('[NewsletterConfirm] failed', { error: error?.message, reason: res?.reason });
        fail();
        return;
      }
      toast.success(lCommon('newsletter_confirm.success', { defaultValue: "You're subscribed to the weekly briefing" }));
    })();
  }, [lCommon]);
}
